import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { AuthService } from '../core/service/auth.service';

interface LoginFormControls {
  username: FormControl<string>;
  password: FormControl<string>;
  rememberMe: FormControl<boolean>;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private static readonly REMEMBER_KEY = 'remembered_username';

  loginForm: FormGroup<LoginFormControls>;
  loggingIn = false;
  errorMessage = '';
  passwordVisible = false;

  // Opens the user's default mail client with a pre-filled access request.
  readonly requestAccessHref = LoginComponent.buildRequestAccessHref();

  constructor(
    private readonly fb: FormBuilder,
    private readonly auth: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translate: TranslateService,
  ) {
    const savedUsername = localStorage.getItem(LoginComponent.REMEMBER_KEY) ?? '';
    this.loginForm = this.fb.group({
      username: new FormControl(savedUsername, { nonNullable: true, validators: Validators.required }),
      password: new FormControl('', { nonNullable: true, validators: Validators.required }),
      rememberMe: new FormControl(!!savedUsername, { nonNullable: true }),
    });

    if (this.auth.isTokenValid()) {
      this.router.navigateByUrl('/selection');
    }
  }

  private static buildRequestAccessHref(): string {
    const { email, subject, toolUrl } = environment.accessRequest;
    const body = ['Hi,', '', 'User: (enter your email id)', `URL: ${toolUrl}`, 'Issue: Request access'].join('\r\n');
    return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  get usernameCtrl() {
    return this.loginForm.controls.username;
  }
  get passwordCtrl() {
    return this.loginForm.controls.password;
  }

  togglePassword(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  logIn(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loggingIn = true;
    this.errorMessage = '';

    const rememberMe = this.loginForm.controls.rememberMe.value;
    if (rememberMe) {
      localStorage.setItem(LoginComponent.REMEMBER_KEY, this.usernameCtrl.value);
    } else {
      localStorage.removeItem(LoginComponent.REMEMBER_KEY);
    }

    this.auth.login(this.usernameCtrl.value, this.passwordCtrl.value).subscribe({
      next: (loginResponse) => {
        localStorage.setItem('token', loginResponse.authentication.token);
        localStorage.setItem('expires_at', JSON.stringify(loginResponse.authentication.validTo));
        this.auth.setUser(loginResponse.feUser);
        this.router.navigateByUrl(this.safeRedirectUrl());
      },
      error: () => {
        this.loggingIn = false;
        this.errorMessage = this.translate.instant('LOGIN.ERROR.AUTHENTICATION_ERROR');
      },
    });
  }

  // Only allow same-app relative redirects — an absolute/external URL here would let an attacker bounce the user off-site after login (open redirect).
  private safeRedirectUrl(): string {
    const redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl');
    return redirectUrl?.startsWith('/') && !redirectUrl.startsWith('//') ? redirectUrl : '/selection';
  }
}
